/**
 * InputForm component for creating new datasets.
 * This component provides a form interface for users to input description
 * and upload required CSV files.
 */

import React, { useState, useEffect, useRef } from "react";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import { Form, SimpleItem, GroupItem, Label } from "devextreme-react/form";
import { Button } from "devextreme-react/button";
import paperPlane from "assets/img/icons/upload.png";
import notify from "devextreme/ui/notify";
import { horusApi } from "../../../../../services/api/horus";

/**
 * Default popup configuration for the InputForm component
 */
export const popupConfigs = {
  width: "80vh",
  height: "auto",
  title: "New Dataset",
  icon: "add",
};

/**
 * InputForm component allows users to create new datasets with description
 * and required file uploads.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isOpen - Controls the visibility of the popup
 * @param {Function} props.onSuccess - Callback function when operation is successful
 */
const InputForm = ({ isOpen, onSuccess }) => {
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState({
    staticData: null,
    ead: null,
  });
  const [fileErrors, setFileErrors] = useState({
    staticData: null,
    ead: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const api = horusApi();

  const resetForm = () => {
    setDescription("");
    setFiles({
      staticData: null,
      ead: null,
    });
    setFileErrors({
      staticData: null,
      ead: null,
    });
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onSuccess();
    }
  };

  const handleFileChange = (field) => (event) => {
    const file = event.target.files[0];
    validateAndSetFile(field, file);
  };

  const validateAndSetFile = (field, file) => {
    if (!file) return;
    const isCsvMime = file.type === "text/csv";
    const isCsvExt =
      typeof file.name === "string" && file.name.toLowerCase().endsWith(".csv");
    const isCsv = isCsvMime || isCsvExt;

    if (!isCsv) {
      setFiles((prev) => ({
        ...prev,
        [field]: file,
      }));
      setFileErrors((prev) => ({
        ...prev,
        [field]: "File type is not allowed",
      }));
      return;
    }
    setFiles((prev) => ({
      ...prev,
      [field]: file,
    }));
    setFileErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const handleFileDrop = (field) => (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    const fileList = event.dataTransfer?.files;
    const file = fileList && fileList[0];
    validateAndSetFile(field, file);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!description) {
      notify("Please fill in all fields to continue", "error", 3000);
      return;
    }

    // Check if all required files are selected
    const requiredFiles = ["staticData", "ead"];
    const missingFiles = requiredFiles.some((field) => !files[field]);

    if (missingFiles) {
      notify("Please fill in all fields to continue", "error", 3000);
      return;
    }

    if (Object.values(fileErrors).some((error) => error !== null)) {
      notify("Please correct file errors before continuing", "error", 3000);
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);
      await api.addDataset({
        description,
        staticData: files.staticData,
        ead: files.ead,
      });
      handleClose();
    } catch (error) {
      notify(error.message || "An error occurred", "error", 3000);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  /**
   * File input component for selecting and displaying a file
   *
   * @param {Object} props - Component properties
   * @param {string} props.label - Label text for the file input
   * @param {string} props.fieldKey - Key identifier for the file field
   * @returns {JSX.Element} - Rendered file input component
   */
  const FileInput = ({ label, fieldKey }) => {
    const inputRef = useRef(null);
    const hasError = fileErrors[fieldKey] !== null;

    return (
      <SimpleItem>
        <Label text={label} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "4px",
            marginTop: "4px",
            border: "1px dashed #cbd5e1",
            borderRadius: "6px",
            padding: "8px",
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop(fieldKey)}
        >
          <Button
            stylingMode="contained"
            text="Select file"
            icon={paperPlane}
            onClick={() => inputRef.current?.click()}
            disabled={isSubmitting}
          />
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange(fieldKey)}
            style={{ display: "none" }}
            disabled={isSubmitting}
          />
          <div style={{ fontSize: "13px", color: "#666", marginLeft: "4px" }}>
            {files[fieldKey]?.name || "No file selected"}
            {hasError && (
              <div
                style={{ color: "#DC2626", fontSize: "12px", marginTop: "2px" }}
              >
                {fileErrors[fieldKey]}
              </div>
            )}
          </div>
          <div
            style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "4px" }}
          >
            Drop a .csv file here
          </div>
        </div>
      </SimpleItem>
    );
  };

  return (
    <PopupWrapper
      {...popupConfigs}
      isVisible={isOpen}
      onCancel={handleClose}
      onConfirm={handleSubmit}
      isConfirmDisabled={isSubmitting}
      isCancelDisabled={isSubmitting}
      showLoading={isLoading}
    >
      <Form ref={formRef}>
        {/* Basic Information Section */}
        <GroupItem caption="Informations" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="description"
            editorType="dxTextBox"
            editorOptions={{
              value: description,
              onValueChanged: (e) => setDescription(e.value),
              placeholder: "Enter description",
              readOnly: isSubmitting,
            }}
            isRequired={true}
          >
            <Label text="Description" />
          </SimpleItem>
        </GroupItem>

        {/* File Upload Section */}
        <GroupItem
          caption="Upload input"
          colCount={2}
          stylingMode="outlined"
          cssClass="dx-field-item-content-transparent"
        >
          <FileInput label="Static data" fieldKey="staticData" />
          <FileInput label="EAD" fieldKey="ead" />
        </GroupItem>
      </Form>
    </PopupWrapper>
  );
};

export default InputForm;
